import type { OpenSplitViewPayload } from '@saaga/shared/store/slices/split-view.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback } from 'react'
import type {
  ChatContentBrief,
  ChatMessage,
  ChatToolCall,
  ChatUserInteraction,
  ReportClickMessage,
} from '@/components/chat/chat-message.interface'
import type {
  ContentBriefData,
  ContentBriefDownloadFormat,
  ParagraphFeedback,
} from '@/components/split-view/content-types/content-brief/content-brief.interface'
import { useSplitView } from '@/hooks/use-split-view.hook'
import { downloadBlobAsFile, downloadContentAsFile } from '@/utils/file-download.util'
import { fetchReportPdfBlob } from '@/utils/pdf-download.helper'

import {
  getContentDataForTool,
  getContentTypeForTool,
  MOCK_CONVERSATION_ID,
} from './chat.view.mock'

const TEXT_SNIPPET_MAX_LENGTH = 200

const truncateSnippet = (text: string): string =>
  text.length <= TEXT_SNIPPET_MAX_LENGTH
    ? text
    : `${text.slice(0, TEXT_SNIPPET_MAX_LENGTH).trim()}…`

const formatFeedbackAsMessage = (
  feedbackList: ParagraphFeedback[],
  additionalComment: string
): string => {
  const parts: string[] = ['**Report feedback:**', '']
  feedbackList.forEach((item, index) => {
    parts.push(`${index + 1}. On: "${truncateSnippet(item.text)}"`)
    parts.push(`   Comment: ${item.comment}`)
    parts.push('')
  })
  if (additionalComment) {
    parts.push(`Additional notes: ${additionalComment}`)
  }
  return parts.join('\n').trimEnd()
}

export interface UseChatSplitViewReturn {
  splitView: ReturnType<typeof useSplitView>
  handleToolCallClick: (toolCall: ChatToolCall) => void
  handleContentBriefClick: (contentBrief: ChatContentBrief) => void
  handleMessageClick: (message: ChatMessage) => void
  handleReportClick: (reportMessage: ReportClickMessage) => void
  handleUserInteractionClick: (interaction: ChatUserInteraction) => void
  handleCloseSplitView: () => void
}

type FeedbackSubmitter = (feedbackList: ParagraphFeedback[], additionalComment: string) => void

type DownloadHandler = (
  content: string,
  filename: string,
  format: ContentBriefDownloadFormat
) => void

interface CreateContentBriefDataOptions {
  contentBrief: ChatContentBrief
  submitFeedback: FeedbackSubmitter
  downloadReport: DownloadHandler
  isHtml?: boolean
  isReadOnly?: boolean
}

const createContentBriefData = ({
  contentBrief,
  submitFeedback,
  downloadReport,
  isHtml,
  isReadOnly,
}: CreateContentBriefDataOptions): ContentBriefData => ({
  id: contentBrief.id,
  name: contentBrief.name,
  version: contentBrief.version,
  content: contentBrief.content,
  isHtml,
  isReadOnly,
  onProceed: (feedbackList: ParagraphFeedback[], additionalComment: string) => {
    submitFeedback(feedbackList, additionalComment)
  },
  onDownload: (format) => {
    downloadReport(contentBrief.content, contentBrief.name, format)
  },
  onCopy: () => {
    logger.debug('Content Brief - Copy clicked')
  },
})

const buildToolCallConfig = (toolCall: ChatToolCall): OpenSplitViewPayload => ({
  contextId: MOCK_CONVERSATION_ID,
  contentType: getContentTypeForTool(toolCall.toolName),
  contentId: toolCall.id,
  contentData: getContentDataForTool(toolCall),
})

const buildContentBriefConfig = (
  contentBrief: ChatContentBrief,
  submitFeedback: FeedbackSubmitter,
  downloadReport: DownloadHandler
): OpenSplitViewPayload => ({
  contextId: MOCK_CONVERSATION_ID,
  contentType: 'content-brief',
  contentId: contentBrief.id,
  contentData: createContentBriefData({ contentBrief, submitFeedback, downloadReport }),
})

const buildUserInteractionConfig = (interaction: ChatUserInteraction): OpenSplitViewPayload => ({
  contextId: MOCK_CONVERSATION_ID,
  contentType: 'user-interaction',
  contentId: interaction.id,
  contentData: { interaction },
})

interface UseChatSplitViewParams {
  onSendMessage?: (content: string) => void
}

const useFeedbackSubmit = (
  splitView: ReturnType<typeof useSplitView>,
  onSendMessage?: (content: string) => void
): FeedbackSubmitter => {
  const conversationId = useAppSelector((s) => s.chat.activeConversationId)
  return useCallback(
    (feedbackList, additionalComment) => {
      if (!conversationId) return
      splitView.close()
      onSendMessage?.(formatFeedbackAsMessage(feedbackList, additionalComment))
    },
    [conversationId, splitView, onSendMessage]
  )
}

const useReportClickHandler = (
  splitView: ReturnType<typeof useSplitView>,
  handleFeedbackSubmit: FeedbackSubmitter,
  downloadReport: DownloadHandler
): ((reportMessage: ReportClickMessage) => void) =>
  useCallback(
    (reportMessage: ReportClickMessage) => {
      const agentName = reportMessage.participant?.name ?? 'Agent'
      const content = reportMessage.content || ''
      const version = String(reportMessage.reportVersion ?? 1)
      const isMaster = reportMessage.isMasterArtifact === true
      const isHtml = reportMessage.reportFormat === 'html'
      const brief: ChatContentBrief = {
        id: reportMessage.id,
        type: 'content-brief',
        name: `${agentName} Report`,
        version,
        content,
        excerpt: '',
        timestamp: reportMessage.timestamp,
      }
      splitView.open({
        contextId: MOCK_CONVERSATION_ID,
        contentType: 'content-brief',
        contentId: reportMessage.id,
        contentData: createContentBriefData({
          contentBrief: brief,
          submitFeedback: handleFeedbackSubmit,
          downloadReport,
          isHtml,
          isReadOnly: !isMaster,
        }),
      })
    },
    [splitView, handleFeedbackSubmit, downloadReport]
  )

const useReportDownloader = (): DownloadHandler =>
  useCallback((content, filename, format) => {
    if (format !== 'pdf') {
      downloadContentAsFile(content, filename, format)
      return
    }
    fetchReportPdfBlob(content)
      .then((blob) => downloadBlobAsFile(blob, filename, 'pdf'))
      .catch((err: unknown) => logger.error('PDF download failed', err))
  }, [])

export const useChatSplitView = (params?: UseChatSplitViewParams): UseChatSplitViewReturn => {
  const splitView = useSplitView()
  const handleFeedbackSubmit = useFeedbackSubmit(splitView, params?.onSendMessage)
  const downloadReport = useReportDownloader()

  const handleToolCallClick = useCallback(
    (toolCall: ChatToolCall) => splitView.open(buildToolCallConfig(toolCall)),
    [splitView]
  )

  const handleContentBriefClick = useCallback(
    (contentBrief: ChatContentBrief) =>
      splitView.open(buildContentBriefConfig(contentBrief, handleFeedbackSubmit, downloadReport)),
    [splitView, handleFeedbackSubmit, downloadReport]
  )
  const handleMessageClick = useCallback((_message: ChatMessage) => {}, [])
  const handleReportClick = useReportClickHandler(splitView, handleFeedbackSubmit, downloadReport)
  const handleUserInteractionClick = useCallback(
    (interaction: ChatUserInteraction) => splitView.open(buildUserInteractionConfig(interaction)),
    [splitView]
  )
  const handleCloseSplitView = useCallback(() => splitView.close(), [splitView])

  return {
    splitView,
    handleToolCallClick,
    handleContentBriefClick,
    handleMessageClick,
    handleReportClick,
    handleUserInteractionClick,
    handleCloseSplitView,
  }
}
